import React, { useState, useRef, useEffect } from 'react';
import { useI18n } from '@/i18n';
import { cn } from '@/lib/utils';
import { ChevronDown, Globe } from 'lucide-react';

interface LanguageSwitcherProps {
  variant?: 'default' | 'compact' | 'mobile';
  className?: string;
}

export const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ 
  variant = 'default',
  className 
}) => {
  const { language, setLanguage, languages, getCurrentLanguageInfo, isRTL } = useI18n();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const currentLang = getCurrentLanguageInfo();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close on escape
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  const handleLanguageChange = (langCode: string) => {
    setLanguage(langCode as typeof language);
    setIsOpen(false);
  };

  if (variant === 'compact') {
    return (
      <div ref={dropdownRef} className={cn("relative", className)}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex min-h-[44px] min-w-[44px] items-center gap-1 px-2 py-1 rounded-md hover:bg-accent/10 transition-colors"
          aria-label="Select language"
          aria-expanded={isOpen}
        >
          <Globe className="h-4 w-4" />
          <span className="text-sm font-medium uppercase">{language}</span>
        </button>
        
        {isOpen && (
          <div className={cn(
            "absolute top-full mt-1 bg-background border border-border rounded-lg shadow-lg py-1 min-w-[120px] z-50",
            isRTL ? "left-0" : "right-0"
          )}>
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code)}
                className={cn(
                  "w-full px-3 py-2 text-sm text-left hover:bg-accent/10 transition-colors flex items-center gap-2",
                  language === lang.code && "bg-accent/5 font-medium"
                )}
              >
                <span>{lang.flag}</span>
                <span>{lang.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (variant === 'mobile') {
    return (
      <div className={cn("w-full", className)}>
        <label className="block text-sm font-medium text-muted-foreground mb-2">
          Language / Idioma
        </label>
        <div className="grid grid-cols-2 gap-2">
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => handleLanguageChange(lang.code)}
              className={cn(
                "flex items-center gap-2 px-3 py-2 rounded-lg border transition-all",
                language === lang.code 
                  ? "border-accent bg-accent/10 text-accent-foreground" 
                  : "border-border hover:border-accent/50"
              )}
            >
              <span className="text-lg">{lang.flag}</span>
              <span className="text-sm font-medium">{lang.label}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // Default dropdown variant
  return (
    <div ref={dropdownRef} className={cn("relative", className)}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "flex items-center gap-2 px-3 py-2 rounded-lg border border-border/50",
          "hover:border-accent/50 hover:bg-accent/5 transition-all",
          "focus:outline-none focus:ring-2 focus:ring-accent/30"
        )}
        aria-label="Select language"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span className="text-lg" aria-hidden="true">{currentLang.flag}</span>
        <span className="text-sm font-medium hidden sm:inline">{currentLang.label}</span>
        <ChevronDown className={cn(
          "h-4 w-4 transition-transform duration-200",
          isOpen && "rotate-180"
        )} />
      </button>
      
      {isOpen && (
        <div 
          className={cn(
            "absolute top-full mt-2 bg-background border border-border rounded-xl shadow-xl py-2 min-w-[180px] z-50",
            "animate-in fade-in-0 zoom-in-95 duration-200",
            isRTL ? "left-0" : "right-0"
          )}
          role="listbox"
          aria-label="Available languages"
        >
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => handleLanguageChange(lang.code)}
              role="option"
              aria-selected={language === lang.code}
              className={cn(
                "w-full px-4 py-2.5 text-left hover:bg-accent/10 transition-colors flex items-center gap-3",
                language === lang.code && "bg-accent/5"
              )}
            >
              <span className="text-xl" aria-hidden="true">{lang.flag}</span>
              <span className={cn(
                "flex-1",
                language === lang.code ? "font-semibold" : "font-medium"
              )}>
                {lang.label}
              </span>
              {language === lang.code && (
                <svg className="h-4 w-4 text-accent" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
